/**
 * Trainova AI — UserTable Component
 * Responsive User Directory Table & Mobile Card View
 */

import { useState } from 'react';
import { TenantUser } from '../types';
import { RoleSelector } from './RoleSelector';
import { useTranslation } from '../../../i18n';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

interface UserTableProps {
  users: TenantUser[];
  onRoleChange: (user: TenantUser, newRole: string) => void;
  onToggleStatus: (user: TenantUser) => void;
  onEditUser?: (user: TenantUser) => void;
  onResetPassword?: (user: TenantUser) => void;
  canManageUsers: boolean;
}

export function UserTable({ users, onRoleChange, onToggleStatus, onEditUser, onResetPassword, canManageUsers }: UserTableProps) {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return 'cards';
    }
    return 'table';
  });

  const {
    currentPage,
    pageSize,
    totalItems,
    paginatedItems: paginatedUsers,
    goToPage,
    setPageSize,
  } = usePagination(users, 1, 20);

  if (users.length === 0) {
    return (
      <div className="admin-empty-state">
        <p>{t('table.no_users', undefined, 'No matching users found.')}</p>
      </div>
    );
  }

  return (
    <div className="user-directory-wrapper">
      {/* View Mode Toggle Bar */}
      <div className="user-directory-toolbar">
        <div className="user-directory-count">
          Showing <strong>{totalItems}</strong> {totalItems === 1 ? 'member' : 'members'}
        </div>
        <div className="user-directory-view-toggle" role="group" aria-label="Directory View Mode">
          <button
            type="button"
            className={`user-directory-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
            onClick={() => setViewMode('cards')}
            title="Card View (optimized for mobile)"
            aria-pressed={viewMode === 'cards'}
          >
            <span>📇</span>
            <span className="user-directory-toggle-label">Cards</span>
          </button>
          <button
            type="button"
            className={`user-directory-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
            onClick={() => setViewMode('table')}
            title="Table View (full spreadsheet with horizontal scrolling)"
            aria-pressed={viewMode === 'table'}
          >
            <span>📋</span>
            <span className="user-directory-toggle-label">Table</span>
          </button>
        </div>
      </div>

      {viewMode === 'cards' ? (
        /* Mobile Card View */
        <div className="user-directory-cards">
          {paginatedUsers.map((u) => (
            <div key={u.id} className="user-directory-card">
              <div className="user-directory-card__header">
                <div className="admin-user-cell">
                  <div className="admin-user-avatar">
                    {u.firstName ? u.firstName[0].toUpperCase() : u.email[0].toUpperCase()}
                  </div>
                  <div className="user-directory-card__title-group">
                    <strong className="admin-user-name">
                      {u.firstName} {u.lastName}
                    </strong>
                    <div className="admin-user-email">{u.email}</div>
                  </div>
                </div>
                <span
                  className={`admin-status-badge ${
                    u.isActive ? 'admin-status-badge--active' : 'admin-status-badge--suspended'
                  }`}
                >
                  <span className="admin-status-dot" />
                  {u.isActive ? 'Active' : 'Suspended'}
                </span>
              </div>

              <div className="user-directory-card__grid">
                <div className="user-directory-card__field">
                  <span className="user-directory-card__label">{t('table.emp_id', undefined, 'Emp ID')}</span>
                  <code className="user-directory-card__code">{u.employeeId || '—'}</code>
                </div>
                <div className="user-directory-card__field">
                  <span className="user-directory-card__label">{t('table.designation', undefined, 'Designation')}</span>
                  <span className="user-directory-card__val">
                    {u.designation || '—'}
                    {u.grade && <small className="user-directory-card__subval">({u.grade})</small>}
                  </span>
                </div>
                <div className="user-directory-card__field">
                  <span className="user-directory-card__label">{t('table.reporting_manager', undefined, 'Manager')}</span>
                  <span className="user-directory-card__val">
                    {u.managerName ? `👔 ${u.managerName}` : '—'}
                  </span>
                </div>
                <div className="user-directory-card__field">
                  <span className="user-directory-card__label">{t('table.team', undefined, 'Team')} / {t('table.region', undefined, 'Region')}</span>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {u.team && (
                      <span className="user-directory-card__team-badge">{u.team}</span>
                    )}
                    {u.region && (
                      <span className="user-directory-card__region-text">{u.region}</span>
                    )}
                    {!u.team && !u.region && <span style={{ color: 'var(--faint)' }}>—</span>}
                  </div>
                </div>
              </div>

              <div className="user-directory-card__role-row">
                <span className="user-directory-card__label" style={{ fontWeight: 600 }}>{t('table.role', undefined, 'Role')}:</span>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  {canManageUsers ? (
                    <RoleSelector
                      currentRole={u.role}
                      onChange={(newRole) => onRoleChange(u, newRole)}
                    />
                  ) : (
                    <span className="admin-role-badge">{u.role}</span>
                  )}
                </div>
              </div>

              {canManageUsers && (
                <div className="user-directory-card__actions">
                  {onEditUser && (
                    <button
                      type="button"
                      onClick={() => onEditUser(u)}
                      className="admin-btn admin-btn--secondary admin-btn--sm"
                      title="Edit member designation, team, region, and role"
                    >
                      ✏️ Edit
                    </button>
                  )}
                  {onResetPassword && (
                    <button
                      type="button"
                      onClick={() => onResetPassword(u)}
                      className="admin-btn admin-btn--secondary admin-btn--sm"
                      title="Reset password or generate reset link"
                    >
                      🔑 Reset Pwd
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleStatus(u)}
                    className={`admin-btn admin-btn--sm ${
                      u.isActive ? 'admin-btn--warning' : 'admin-btn--primary'
                    }`}
                  >
                    {u.isActive ? 'Suspend' : 'Activate'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Full Spreadsheet / Table View with Contained Horizontal Scrolling */
        <>
          <div className="admin-table-mobile-hint" style={{ marginBottom: '8px' }}>
            <span>↔ Swipe horizontally to view all columns &amp; actions</span>
          </div>
          <div className="admin-table-container">
            <table className="admin-table user-directory-table">
              <thead>
                <tr>
                  <th>{t('table.member', undefined, 'Member')}</th>
                  <th>{t('table.emp_id', undefined, 'Emp ID')}</th>
                  <th>{t('table.designation', undefined, 'Designation')}</th>
                  <th>{t('table.reporting_manager', undefined, 'Reporting Manager')}</th>
                  <th>{t('table.team', undefined, 'Team')}</th>
                  <th>{t('table.region', undefined, 'Region')}</th>
                  <th>{t('table.role', undefined, 'Role')}</th>
                  <th>{t('table.status', undefined, 'Status')}</th>
                  {canManageUsers && <th className="user-directory-col-actions">{t('table.actions', undefined, 'Actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div className="admin-user-cell">
                        <div className="admin-user-avatar">
                          {u.firstName ? u.firstName[0].toUpperCase() : u.email[0].toUpperCase()}
                        </div>
                        <div>
                          <strong className="admin-user-name">
                            {u.firstName} {u.lastName}
                          </strong>
                          <div className="admin-user-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <code style={{ fontSize: '12px', background: 'var(--surface)', padding: '2px 6px', borderRadius: '4px' }}>
                        {u.employeeId || '—'}
                      </code>
                    </td>
                    <td>
                      <span style={{ fontWeight: 500, color: u.designation ? 'var(--navy)' : 'var(--faint)' }}>
                        {u.designation || '—'}
                      </span>
                      {u.grade && (
                        <span style={{ display: 'block', fontSize: '11px', color: 'var(--muted)' }}>
                          {u.grade}
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '14px', opacity: u.managerName ? 0.9 : 0.3 }}>👔</span>
                        <span style={{ color: u.managerName ? 'var(--ink)' : 'var(--faint)', fontSize: '12.5px', fontWeight: u.managerName ? 500 : 400 }}>
                          {u.managerName || 'Not Assigned'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '11.5px',
                          fontWeight: 600,
                          background: u.team ? 'var(--blue-soft)' : 'transparent',
                          color: u.team ? 'var(--blue)' : 'var(--faint)',
                        }}
                      >
                        {u.team || '—'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: u.region ? 'var(--ink)' : 'var(--faint)', fontSize: '12.5px' }}>
                        {u.region || '—'}
                      </span>
                    </td>
                    <td>
                      {canManageUsers ? (
                        <RoleSelector
                          currentRole={u.role}
                          onChange={(newRole) => onRoleChange(u, newRole)}
                        />
                      ) : (
                        <span className="admin-role-badge">{u.role}</span>
                      )}
                    </td>
                    <td>
                      <span
                        className={`admin-status-badge ${
                          u.isActive ? 'admin-status-badge--active' : 'admin-status-badge--suspended'
                        }`}
                      >
                        <span className="admin-status-dot" />
                        {u.isActive ? 'Active' : 'Suspended'}
                      </span>
                    </td>
                    {canManageUsers && (
                      <td className="user-directory-col-actions">
                        <div className="admin-table-actions">
                          {onEditUser && (
                            <button
                              type="button"
                              onClick={() => onEditUser(u)}
                              className="admin-btn admin-btn--secondary admin-btn--sm"
                              title="Edit member designation, team, region, and role"
                            >
                              Edit
                            </button>
                          )}
                          {onResetPassword && (
                            <button
                              type="button"
                              onClick={() => onResetPassword(u)}
                              className="admin-btn admin-btn--secondary admin-btn--sm"
                              title="Reset password or generate reset link"
                            >
                              🔑 Reset Pwd
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => onToggleStatus(u)}
                            className={`admin-btn admin-btn--sm ${
                              u.isActive ? 'admin-btn--warning' : 'admin-btn--primary'
                            }`}
                          >
                            {u.isActive ? 'Suspend' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={goToPage}
          onPageSizeChange={setPageSize}
          showPageSizeSelector
          pageSizeOptions={[10, 20, 50, 100]}
          itemLabel="members"
        />
      )}
    </div>
  );
}

